import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ApiError } from '@/api/client'
import type { SaveStatus } from '@/hooks/saveStatus'
import { platformApi } from '../api'
import { activePages, missingOnPage, missingRequired } from '../lib/form'
import type { Answers, FormDefinition, Submission } from '../types'

const DEBOUNCE_MS = 1200

export interface FormRun {
    loading: boolean
    error: string | null
    submission: Submission | null
    definition: FormDefinition | null
    answers: Answers
    title: string
    setTitle: (t: string) => void
    setAnswer: (key: string, value: unknown) => void
    pages: ReturnType<typeof activePages>
    /** Index into `pages`; pages.length is the review step. */
    step: number
    goTo: (i: number) => void
    next: () => boolean
    back: () => void
    invalidKeys: string[]
    allMissing: string[]
    saveStatus: SaveStatus
    submitting: boolean
    submitError: string | null
    submit: () => Promise<Submission | null>
}

/**
 * Drives filling in a draft submission: loads the pinned definition, keeps
 * answers locally, autosaves (debounced PATCH), gates "Next" on the current
 * page's required questions, and submits (the server re-validates everything).
 */
export function useFormRun(submissionId: string): FormRun {
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [submission, setSubmission] = useState<Submission | null>(null)
    const [definition, setDefinition] = useState<FormDefinition | null>(null)
    const [answers, setAnswers] = useState<Answers>({})
    const [title, setTitleState] = useState('')
    const [step, setStep] = useState(0)
    const [invalidKeys, setInvalidKeys] = useState<string[]>([])
    const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
    const [submitting, setSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)

    const latest = useRef({ answers, title, step })
    latest.current = { answers, title, step }
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const dirty = useRef(false)

    useEffect(() => {
        let cancelled = false
        setLoading(true)
        platformApi
            .getSubmission(submissionId)
            .then((d) => {
                if (cancelled) return
                setSubmission(d.submission)
                setDefinition(d.definition)
                setAnswers(d.submission.answers ?? {})
                setTitleState(d.submission.title)
                setStep(Math.min(d.submission.current_page, activePages(d.definition, d.submission.answers ?? {}).length))
                setLoading(false)
            })
            .catch((e) => {
                if (cancelled) return
                setError(e instanceof Error ? e.message : 'Could not load the form')
                setLoading(false)
            })
        return () => { cancelled = true }
    }, [submissionId])

    const save = useCallback(async () => {
        if (!dirty.current) return
        dirty.current = false
        const { answers: a, title: t, step: s } = latest.current
        setSaveStatus('saving')
        try {
            await platformApi.saveSubmission(submissionId, { title: t, answers: a, current_page: s })
            setSaveStatus('saved')
        } catch (e) {
            dirty.current = true
            setSaveStatus('error')
            if (e instanceof ApiError && e.status === 409) setError('This submission was already submitted.')
        }
    }, [submissionId])

    const schedule = useCallback(() => {
        dirty.current = true
        if (timer.current) clearTimeout(timer.current)
        timer.current = setTimeout(save, DEBOUNCE_MS)
    }, [save])

    // Flush a pending save when leaving the page.
    useEffect(() => () => {
        if (timer.current) clearTimeout(timer.current)
        if (dirty.current) void save()
    }, [save])

    const setAnswer = useCallback((key: string, value: unknown) => {
        setAnswers((prev) => ({ ...prev, [key]: value }))
        setInvalidKeys((k) => k.filter((x) => x !== key))
        schedule()
    }, [schedule])

    const setTitle = useCallback((t: string) => { setTitleState(t); schedule() }, [schedule])

    const pages = useMemo(() => (definition ? activePages(definition, answers) : []), [definition, answers])
    const allMissing = useMemo(() => (definition ? missingRequired(definition, answers) : []), [definition, answers])

    const goTo = useCallback((i: number) => {
        setStep(Math.max(0, Math.min(i, pages.length)))
        setInvalidKeys([])
        schedule()
    }, [pages.length, schedule])

    const next = useCallback(() => {
        const page = pages[step]
        if (page) {
            const miss = missingOnPage(page, answers)
            if (miss.length > 0) {
                setInvalidKeys(miss)
                document.getElementById(`field-${miss[0]}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                return false
            }
        }
        goTo(step + 1)
        return true
    }, [pages, step, answers, goTo])

    const back = useCallback(() => goTo(step - 1), [goTo, step])

    const submit = useCallback(async () => {
        setSubmitError(null)
        setSubmitting(true)
        try {
            if (timer.current) clearTimeout(timer.current)
            dirty.current = true
            await save()
            const done = submission?.status === 'returned'
                ? await platformApi.resubmit(submissionId)
                : await platformApi.submit(submissionId)
            setSubmission(done)
            return done
        } catch (e) {
            setSubmitError(e instanceof Error ? e.message : 'Submit failed')
            return null
        } finally {
            setSubmitting(false)
        }
    }, [save, submissionId, submission?.status])

    return {
        loading, error, submission, definition, answers, title, setTitle, setAnswer, pages, step, goTo, next, back,
        invalidKeys, allMissing, saveStatus, submitting, submitError, submit,
    }
}
